import React from "react"
import * as NumericInputField from "react-numeric-input";
import { Link as RouterLink } from "react-router-dom"

import {
    css,
    ColorPicker,
    Theme,
    Div,
    FieldLabel,
    InputGroup,
    Icon,
    Grid,
    GridCell,
} from "@bdfx/mergado-ui-kit"

import trans, { isTrans } from "trans"
import store from "store";
import TransparentColor from "./transparent-color";
import {
    updateInputValue,
} from "app/reducers/form";
import { iconDisabledColor } from "template-edit/components/tools-panel";
import { debounce, find, sortBy } from "lodash";
import { AlternativeTemplate, AlternativeTemplatesLevels } from "app/types/model-types";
import { invertColor } from "app/helpers/other";

const { abs, sin, cos, PI, max, min, round, atan2, atan, ceil } = Math

const updateValueDebounced = debounce((formName, subFormName, value) => {
    updateValueNow(formName, subFormName, value)
}, 600)

const updateValueNow = (formName, subFormName, value) => {
    store.dispatch(updateInputValue(formName, subFormName, value))
}

export const Color = ({ formName }) => (
    <ColorPicker
        name={`${formName}.color`}
        label={trans("color")}
    />
)

export const ColorField = ({ formName, fieldName, props }) => (
    <TransparentColor
        name={`${formName}.${fieldName}`}
        value={props[formName].shapes[props.activeShapeId][fieldName] || null}
        changeValue={(color) => props.change(formName, `shapes.${props.activeShapeId}.${fieldName}`, color)}
    />
)

export const StyledInputGroupFilter = ({formName, transString, iconType, singleChoice, items, showClear, onClear, onChange }) => (
    <InputGroup
        style={{
            /* whiteSpace: 'nowrap', */
            fontSize: 'small',
            mozUserSelect: '-moz-none',
            webkitUserSelect: 'none',
            msUserSelect: 'none',
            userSelect: 'none',
            alignSelf: 'center',
        }}
        name={formName}
        singleChoice={singleChoice}
        hideInput={true}
        items={items}
        label={<div>
            <Icon type={iconType} text={trans(transString)} />
            {showClear &&
                <span
                    className="remove clickable"
                    onClick={onClear}
                    style={{ position: 'absolute', right: '10px', background: '#ffffffee', borderRadius: '50%', width: '20px', textAlign: 'center' }}>
                    <Icon type={'close-light'} /></span>
            }
        </div>}
        onChange={onChange}
    />
)

export const GradientDefs = ({ gradient, idx }) => {
    const stopPoints = typeof gradient.stop_points === 'string' ? JSON.parse(gradient.stop_points) : gradient.stop_points
    const sortedStopPoints = stopPoints ? sortBy(stopPoints, [(stopPoint) => {
        return stopPoint.offset
    }]) : []

    const renderStops = sortedStopPoints.map((stopPoint, key) => <stop key={`stop-${idx}-${key}`} stopColor={stopPoint.color} offset={`${(stopPoint.offset ?? 0) / 100}`} />)

    return <defs>
        {gradient.type === 'linear' &&
            <linearGradient
                spreadMethod="pad"
                y2={`${(gradient.y2 ?? 0) / 100}`}
                x2={`${(gradient.x2 ?? 0) / 100}`}
                y1={`${(gradient.y1 ?? 0) / 100}`}
                x1={`${(gradient.x1 ?? 0) / 100}`}
                id={GradientElementId(idx)}>{renderStops}</linearGradient>
        }

        {gradient.type === 'radial' &&
            <radialGradient
                spreadMethod="pad"
                cx={`${(gradient.x1 ?? 0) / 100}`}
                cy={`${(gradient.y1 ?? 0) / 100}`}
                fx={`${(gradient.x2 ?? 0) / 100}`}
                fy={`${(gradient.y2 ?? 0) / 100}`}
                r={`${(gradient.radius ?? 50) / 100}`}
                id={GradientElementId(idx)}>{renderStops}</radialGradient>
        }
    </defs>
}

export const GradientElementId = (idx) => `gradient-fill-${idx}`
export const GradientFillUrl = (idx) => `url(#gradient-fill-${idx})`

export const GradientFillPreview = ({ gradient, idx, isActive = null }) => {
    return <>
        <GradientDefs gradient={gradient} idx={idx} />
        <rect
            x="0"
            y="0"
            width="256"
            height="256"
            fill={GradientFillUrl(idx)}
            fillOpacity={`${(gradient.opacity ?? 100) / 100}`}
            stroke={isActive ? Theme.blue : '#3a3a3a'}
            strokeWidth={isActive ? 9 : .8}
        />
    </>
}

export const GradientSvg = ({ gradient, idx, width, height, isActive = null, style = undefined }) => {
    return <svg viewBox={'-10 0 300 300'} width={`${width}`} height={`${height}`} xmlns="http://www.w3.org/2000/svg" style={style}>
        <GradientFillPreview gradient={gradient} idx={idx} isActive={isActive} />
    </svg>
}

export const NumericInput = ({
    formName,
    fieldName,
    label = null,
    placeholder = undefined,
    shapeIndex = null,
    value = null,
    step = 1,
    precision = 0,
    min = null,
    max = null,
    id = undefined,
    changeValueMethod = undefined,
    units = null,
    onlyPositive = false,
    asFloat = false,
    onChangeFunction = undefined,
    isMobile = undefined,
    style = undefined
}) => {
    const isMobileDetected = typeof document !== 'undefined' && 'ontouchstart' in document
    const p = isTrans(placeholder) ? trans(placeholder) : placeholder
    let target

    return <StyledNumericInput onClick={(e) => target = e.target.tagName} style={style}>
        {label && <FieldLabel>{label}</FieldLabel>}
        <Div position="relative" title={p}>
            {/* {(!isMobile && !isMobileDetected && placeholder) &&
                <span className={'placeholder'} style={{ width: '100%' }}>{p}</span>
            } */}
            <NumericInputField
                id={id}
                precision={precision && asFloat ? precision : undefined}
                placeholder={p}
                value={value}
                step={step}
                mobile={isMobile}
                format={(num) => units ? `${num} ${units}` : num}
                parse={(stringValue) => units ? parseFloat(stringValue.replace(` ${units}`, '')) : stringValue}
                onChange={(val) => {
                    let newValue = val
                    if (min && val < min) {
                        newValue = min
                    }
                    if (max && val > max) {
                        newValue = max
                    }
                    if (onlyPositive && val < 0) {
                        newValue = abs(newValue)
                    }
                    if (!asFloat) {
                        newValue = round(newValue)
                    }
                    if (typeof changeValueMethod !== 'undefined') {
                        newValue = changeValueMethod(newValue)
                    }

                    const subFormName = shapeIndex ? `shapes.${shapeIndex}.${fieldName}` : fieldName
                    const parsedValue = asFloat ? parseFloat(`${newValue}`) : parseInt(`${newValue}`)

                    if (target === 'INPUT') {
                        updateValueDebounced(formName, subFormName, parsedValue)
                    } else {
                        updateValueNow(formName, subFormName, parsedValue)    //mouse click on +/-
                    }

                    if (typeof onChangeFunction !== 'undefined') {
                        onChangeFunction(newValue)
                    }
                }}
                snap
            />
        </Div>
    </StyledNumericInput>
}

export const ProgressBar = ({ percents = 0, title = '', color = Theme.steelblue, textColor = '#000', size = 10 }) => {
    return <StyledProgressBar title={title} dataSize={size}>
        <StyledPercents dataPercents={percents} dataColor={color} dataTextColor={textColor} dataSize={size}>
            <span className={'percents'}>{`${round(percents * 100) / 100}%`}</span>
        </StyledPercents>
    </StyledProgressBar>
}

export type BlockType = {
    w: number
    h: number
}

export const TextCursor = ({ h, offsetY = 0, block = [] }) => {
    return <g id={'cursor'} stroke={'none'} fill={'none'} style={{ transform: `translate(0,0)`, opacity: '0', pointerEvents: 'none' }}>
        <line y1={-.6 * h + offsetY} y2={.6 * h + offsetY} />
        <animate
            attributeType="XML"
            attributeName="stroke"
            values="#3a3a3a;#ffffff;#cccccc;#3a3a3a"
            dur="1.5s"
            repeatCount="indefinite"
        />
        {block && Array.isArray(block) && block.length > 0 &&
            block.map((b: BlockType, i: number) => {
                return <rect
                    key={`block_${i}`}
                    width={b.w}
                    height={b.h}
                    x={0}
                    y={-.5 * h + offsetY}
                    stroke={'#beb42363'}
                    fill={'#eeeeee81'}
                />
            })
        }
    </g>
}

export const renderAltLevels = (levelsToTemplateLevels: AlternativeTemplatesLevels[], alternativeTemplates: AlternativeTemplate[], linkString: string) => {
    let altIdToColor = {}
    const COLORS = ['#51E5FF','#440381','#37ed46','#FFD6C0']
    return <Grid
        gap={'2px'}
        cols={'repeat(auto-fit, minmax(15px, 1fr))'}
        autoFlow={'row'}
        style={{
            cusor: 'pointer',
            ' .muk-gridcell': {
                textAlign: 'center',
                fontSize: 'small',
                overflow: 'hidden',
            }
        }}>

        {levelsToTemplateLevels.map((m: AlternativeTemplatesLevels, i: number) => {
            if (typeof m === 'undefined' || !m) { return <GridCell key={`m_i${i}`}>-</GridCell> }

            const alternativeTemplate = find(alternativeTemplates, (a: AlternativeTemplate) => {
                return `${m.alternative_template_id}` === `${a.id}`
            })

            if (typeof altIdToColor[`${m.alternative_template_id}`] === 'undefined') {
                altIdToColor[`${m.alternative_template_id}`] = COLORS[Object.keys(altIdToColor).length]
            }

            const isActive = alternativeTemplate.active === 1
            const bgColor = altIdToColor[`${m.alternative_template_id}`]

            return <GridCell
                key={`m_${m.alternative_template_id}`}
                title={`level ${i}: **${alternativeTemplate?.name}** (${m.alternative_template_id}) mode ${trans(m.levels_mode)} ${m.levels ? m.levels : ''} [priority ${m.priority}]`}
                style={{
                    color: isActive ? invertColor(bgColor) : '#3a3a3a',
                    background: isActive ? `${bgColor} !important` : '#ddd !important',
                    textDecoration: isActive ? 'none' : 'line-through'
                }}>

                {/* tady styl pokud neni active */}
                    <RouterLink to={`${linkString}${m.alternative_template_id}`}>
                        {m.alternative_template_id}   {/* m.priority */}
                    </RouterLink>
            </GridCell>
        })}
    </Grid>
}

const StyledProgressBar = css('div')({
    background: '#ced6e6',
    border: '1px solid #7793c1',
    padding: '3px',
    position: 'relative',
}, props => {
    return {
        height: `${props.dataSize}px`,
    }
})

const StyledPercentsAbs = css('div')({
    position: 'absolute',
    border: '1px solid #7793c1',
    ' .percents': {
        position: 'relative',
        width: '10em',
        left: '3px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        overflow: 'overlay',
    },
}, props => {
    return {
        top: '3px',
        left: '3px',
        height: `${props.dataSize - 8}px`,
        lineHeight: `${props.dataSize}px`,
        width: `calc(${props.dataPercents}% - 6px)`,
        background: props.dataColor ?? Theme.blue,
        ' .percents': {
            color: props.dataTextColor ?? '#000',
            height: `${props.dataSize - 6}px`,
            fontSize: `${props.dataSize / 2}px`,
            top: `-${(ceil(props.dataSize / 4) + 1)}px`,
        },
    }
})

const StyledPercents = css('div')({
    border: '1px solid #7793c1',
    ' .percents': {
        position: 'relative',
        width: '10em',
        left: '3px',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
    },
}, props => {
    return {
        height: `${props.dataSize - 8}px`,
        lineHeight: `${props.dataSize}px`,
        width: `${props.dataPercents}%`,
        background: props.dataColor ?? Theme.blue,
        ' .percents': {
            color: props.dataTextColor ?? '#000',
            height: `${props.dataSize - 6}px`,
            fontSize: `${props.dataSize / 2}px`,
            top: `-${(ceil(props.dataSize / 4) + 1)}px`,
        },
    }
})

const StyledNumericInput = css('div')({
    width: '100%',
    ' .react-numeric-input': {
        width: '100%',
    },
    ' input': {
        width: '100%',
        textAlign: 'right',
        height: '40px',
        paddingRight: '40px !important',
    },
    ' .placeholder': {
        pointerEvents: 'none',
        color: iconDisabledColor,
        position: 'absolute',
        top: '10px',
        left: '10px',
        zIndex: 10,
    },
    ' span.react-numeric-input b': {
        fontSize: '20px',
    }
})